import React, { useState, useEffect } from 'react';
import './ShopManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ShopManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeProductTab, setActiveProductTab] = useState('all-products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [filters, setFilters] = useState({});
  const [analyticsPeriod, setAnalyticsPeriod] = useState('7d');
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    type: '',
    category: '',
    price: '',
    stock: '',
    stockStatus: 'in-stock',
    status: 'Active',
    sku: ''
  });

  // Tab configuration for enterprise e-commerce
  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: 'dashboard' },
    { id: 'products', name: 'All Products', icon: 'inventory_2' },
    { id: 'orders', name: 'Orders', icon: 'shopping_cart' },
    { id: 'customers', name: 'Customers', icon: 'people' },
    { id: 'analytics', name: 'Analytics', icon: 'analytics' },
    { id: 'marketing', name: 'Marketing', icon: 'campaign' },
    { id: 'settings', name: 'Settings', icon: 'settings' }
  ];

  // Product sub-tabs configuration
  const productSubTabs = [
    { id: 'all-products', name: 'All Products', icon: 'inventory_2' },
    { id: 'books', name: 'Books & Publications', icon: 'menu_book' },
    { id: 'stationery', name: 'Stationery & Supplies', icon: 'edit' },
    { id: 'technology', name: 'Technology & Electronics', icon: 'computer' },
    { id: 'classroom', name: 'Classroom Equipment', icon: 'school' },
    { id: 'digital-resources', name: 'Digital Resources', icon: 'cloud_download' },
    { id: 'early-childhood', name: 'Early Childhood', icon: 'child_care' },
    { id: 'stem', name: 'STEM Education', icon: 'science' },
    { id: 'arts', name: 'Arts & Creative', icon: 'palette' },
    { id: 'institutional', name: 'Institutional Products', icon: 'business' }
  ];

  // Add mock data on initial load
  useEffect(() => {
    setProducts([
      {
        id: 1,
        name: 'Mathematics Textbook',
        imageUrl: '',
        category: 'Books',
        type: 'books',
        price: 25.99,
        stock: 45,
        stockStatus: 'in-stock',
        status: 'Active',
        sku: 'MATH-001',
      },
      {
        id: 2,
        name: 'Science Kit',
        imageUrl: '',
        category: 'Technology',
        type: 'technology',
        price: 89.99,
        stock: 12,
        stockStatus: 'low-stock',
        status: 'Active',
        sku: 'SCI-002',
      },
      {
        id: 3,
        name: 'Art Supplies Set',
        imageUrl: '',
        category: 'Arts & Creative',
        type: 'arts',
        price: 39.99,
        stock: 30,
        stockStatus: 'in-stock',
        status: 'Active',
        sku: 'ART-003',
      },
      {
        id: 4,
        name: 'Digital English Course',
        imageUrl: '',
        category: 'Digital Resources',
        type: 'digital-resources',
        price: 59.99,
        stock: null,
        stockStatus: 'in-stock',
        status: 'Active',
        sku: 'DIG-004',
      },
      {
        id: 5,
        name: 'Preschool Learning Kit',
        imageUrl: '',
        category: 'Early Childhood',
        type: 'early-childhood',
        price: 49.99,
        stock: 20,
        stockStatus: 'in-stock',
        status: 'Active',
        sku: 'PRE-005',
      },
      {
        id: 6,
        name: 'STEM Robotics Set',
        imageUrl: '',
        category: 'STEM Education',
        type: 'stem',
        price: 129.99,
        stock: 8,
        stockStatus: 'low-stock',
        status: 'Active',
        sku: 'STEM-006',
      },
      {
        id: 7,
        name: 'Institutional Whiteboard',
        imageUrl: '',
        category: 'Institutional Products',
        type: 'institutional',
        price: 199.99,
        stock: 5,
        stockStatus: 'low-stock',
        status: 'Active',
        sku: 'INST-007',
      },
      {
        id: 8,
        name: 'Classroom Projector',
        imageUrl: '',
        category: 'Classroom Equipment',
        type: 'classroom',
        price: 499.99,
        stock: 2,
        stockStatus: 'low-stock',
        status: 'Active',
        sku: 'CLASS-008',
      },
      {
        id: 9,
        name: 'Stationery Pack',
        imageUrl: '',
        category: 'Stationery & Supplies',
        type: 'stationery',
        price: 9.99,
        stock: 100,
        stockStatus: 'in-stock',
        status: 'Active',
        sku: 'STAT-009',
      },
      {
        id: 10,
        name: 'Digital Art Course',
        imageUrl: '',
        category: 'Digital Resources',
        type: 'digital-resources',
        price: 79.99,
        stock: null,
        stockStatus: 'in-stock',
        status: 'Active',
        sku: 'DIG-010',
      },
    ]);
  }, []);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab]);

  const loadTabData = async (tabId) => {
    setLoading(true);
    setError('');
    
    try {
      // Simulate API calls for different tabs with pagination
      switch(tabId) {
        case 'dashboard':
          await loadDashboardData();
          break;
        case 'products':
          await loadProductsData();
          break;
        case 'orders':
          await loadOrdersData();
          break;
        case 'customers':
          await loadCustomersData();
          break;
        default:
          // Load section-specific data
          break;
      }
    } catch (err) {
      console.error('Error loading tab data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Enterprise Dashboard with KPIs
  const loadDashboardData = async () => {
    // Simulate API call for dashboard data
    console.log('Loading dashboard data...');
  };

  const loadProductsData = async () => {
    // Simulate API call with pagination
    console.log('Loading products data...');
  };

  const loadOrdersData = async () => {
    // Simulate API call with pagination
    console.log('Loading orders data...');
  };

  const loadCustomersData = async () => {
    // Simulate API call with pagination
    console.log('Loading customers data...');
  };

  const handleAddProductClick = () => {
    setShowAddForm(true);
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFormSubmit = (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.type || !newProduct.price) return;
    setProducts((prev) => [
      ...prev,
      {
        ...newProduct,
        id: Date.now(),
        price: parseFloat(newProduct.price),
        stock: newProduct.stock ? parseInt(newProduct.stock) : null,
        sku: newProduct.sku || `SKU-${Date.now()}`,
      },
    ]);
    setShowAddForm(false);
    setNewProduct({
      name: '',
      type: '',
      category: '',
      price: '',
      stock: '',
      stockStatus: 'in-stock',
      status: 'Active',
      sku: ''
    });
  };

  const handleAddFormCancel = () => {
    setShowAddForm(false);
    setNewProduct({
      name: '',
      type: '',
      category: '',
      price: '',
      stock: '',
      stockStatus: 'in-stock',
      status: 'Active',
      sku: ''
    });
  };

  const renderDashboard = () => (
    <div className="shop-dashboard">
      {/* KPI Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Sales Today</h3>
          <div className="summary-value">$8,450</div>
          <div className="summary-change positive">+12% from yesterday</div>
        </div>
        <div className="summary-card">
          <h3>Orders Today</h3>
          <div className="summary-value">156</div>
          <div className="summary-change positive">+8% from yesterday</div>
        </div>
        <div className="summary-card">
          <h3>Active Products</h3>
          <div className="summary-value">2,450</div>
          <div className="summary-change neutral">No change</div>
        </div>
        <div className="summary-card">
          <h3>New Customers</h3>
          <div className="summary-value">23</div>
          <div className="summary-change positive">+15% from yesterday</div>
        </div>
      </div>
      
      {/* Inventory Alerts */}
      <div className="alert-section">
        <h3>Inventory Alerts</h3>
        <div className="alert-list">
          <div className="alert-item warning">
            <span className="material-icons">warning</span>
            <span>15 products low in stock</span>
          </div>
          <div className="alert-item danger">
            <span className="material-icons">error</span>
            <span>3 products out of stock</span>
          </div>
        </div>
      </div>
      
      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        <div className="chart-container">
          <h3>Recent Orders</h3>
          <div className="data-list">
            <div className="data-list-item">
              <span>Order #1234 - John Doe</span>
              <span className="status-badge pending">Pending</span>
            </div>
            <div className="data-list-item">
              <span>Order #1235 - Jane Smith</span>
              <span className="status-badge shipped">Shipped</span>
            </div>
            <div className="data-list-item">
              <span>Order #1236 - Mike Johnson</span>
              <span className="status-badge delivered">Delivered</span>
            </div>
          </div>
        </div>
        <div className="chart-container">
          <h3>Top Selling Products</h3>
          <div className="data-list">
            <div className="data-list-item">
              <span>Mathematics Textbook</span>
              <span>45 units sold</span>
            </div>
            <div className="data-list-item">
              <span>Science Kit</span>
              <span>32 units sold</span>
            </div>
            <div className="data-list-item">
              <span>Art Supplies Set</span>
              <span>28 units sold</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Pending Issues */}
      <div className="issues-section">
        <h3>Pending Issues</h3>
        <div className="issues-list">
          <div className="issue-item">
            <span className="material-icons">payment</span>
            <span>2 failed payments</span>
          </div>
          <div className="issue-item">
            <span className="material-icons">support_agent</span>
            <span>5 customer complaints</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProducts = () => {
    // Filter products by selected type
    const filteredProducts = activeProductTab === 'all-products'
      ? products
      : products.filter(product => product.type === activeProductTab);

    return (
      <div className="shop-products">
        <div className="section-header">
          <h1>All Products</h1>
          <div className="header-actions">
            <button className="btn-secondary">Export CSV</button>
            <button className="btn-secondary">Import CSV</button>
            <button className="btn-primary" onClick={handleAddProductClick}>Add Product</button>
          </div>
        </div>
        {/* Add Product Modal Overlay */}
        {showAddForm && (
          <>
            <div
              className="modal-backdrop"
              onClick={handleAddFormCancel}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.35)',
                zIndex: 1000,
              }}
            />
            <form
              className="add-product-form-modal"
              onClick={e => e.stopPropagation()}
              onSubmit={handleAddFormSubmit}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 12,
                padding: 32,
                minWidth: 320,
                maxWidth: 420,
                width: '90vw',
                zIndex: 1001,
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              }}
            >
              <h2 style={{margin: 0, marginBottom: 8, fontSize: 22, textAlign: 'center'}}>Add Product</h2>
              <input
                type="text"
                name="name"
                placeholder="Product Name"
                value={newProduct.name}
                onChange={handleAddFormChange}
                required
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
              />
              <select
                name="type"
                value={newProduct.type}
                onChange={handleAddFormChange}
                required
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
              >
                <option value="">Select Type</option>
                {productSubTabs.filter(tab => tab.id !== 'all-products').map(tab => (
                  <option key={tab.id} value={tab.id}>{tab.name}</option>
                ))}
              </select>
              <input
                type="text"
                name="category"
                placeholder="Category"
                value={newProduct.category}
                onChange={handleAddFormChange}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
              />
              <input
                type="number"
                name="price"
                placeholder="Price"
                value={newProduct.price}
                onChange={handleAddFormChange}
                required
                min="0"
                step="0.01"
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
              />
              <input
                type="number"
                name="stock"
                placeholder="Stock"
                value={newProduct.stock}
                onChange={handleAddFormChange}
                min="0"
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
              />
              <input
                type="text"
                name="sku"
                placeholder="SKU (optional)"
                value={newProduct.sku}
                onChange={handleAddFormChange}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
              />
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>Add</button>
                <button type="button" className="btn-secondary" style={{ flex: 1 }} onClick={handleAddFormCancel}>Cancel</button>
              </div>
            </form>
          </>
        )}
        {/* Product Sub-tabs */}
        <div className="product-sub-tabs">
          {productSubTabs.map((tab) => (
            <button
              key={tab.id}
              className={`product-sub-tab ${activeProductTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveProductTab(tab.id)}
            >
              <span className="material-icons">{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </div>
        {/* Advanced Filters */}
        <div className="filters-bar">
          <input type="text" placeholder="Search products..." className="search-input" />
          <select className="filter-select">
            <option value="">All Categories</option>
            <option value="books">Books</option>
            <option value="stationery">Stationery</option>
            <option value="technology">Technology</option>
          </select>
          <select className="filter-select">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="draft">Draft</option>
          </select>
          <select className="filter-select">
            <option value="">Stock Level</option>
            <option value="in-stock">In Stock</option>
            <option value="low-stock">Low Stock</option>
            <option value="out-of-stock">Out of Stock</option>
          </select>
          <button className="btn-secondary">Clear Filters</button>
          <div className="more-actions-dropdown">
            <button className="btn-secondary more-actions-btn">More Actions <span className="material-icons" style={{fontSize: '18px', verticalAlign: 'middle'}}>expand_more</span></button>
            <div className="more-actions-menu">
              <button className="dropdown-item">Bulk Edit</button>
              <button className="dropdown-item">Bulk Delete</button>
              <button className="dropdown-item">Bulk Publish</button>
            </div>
          </div>
        </div>
        {/* Products Card Grid */}
        <div className="products-grid">
          {filteredProducts.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>
              No products found for this category.
            </div>
          ) : (
            filteredProducts.map((product) => (
              <div className="product-card" key={product.id}>
                <div className="product-image">
                  {/* Optionally use <img src={product.imageUrl} ... /> if available */}
                  <span className="material-icons" style={{fontSize: 64, color: '#e0e0e0', width: '100%', textAlign: 'center', display: 'block', marginTop: 32}}>inventory_2</span>
                </div>
                <div className="product-title">{product.name}</div>
                <div className="product-category">{product.category}</div>
                <div className={`product-status ${product.stockStatus}`}>{product.stockStatus && product.stockStatus.replace('-', ' ')}</div>
                <div className="product-price">${product.price}</div>
                <div className="product-sku" style={{color: '#888', fontSize: 12, marginBottom: 8}}>SKU: {product.sku}</div>
                <div className="product-actions">
                  <button className="btn-icon" title="Edit">
                    <span className="material-icons">edit</span>
                  </button>
                  <button className="btn-icon" title="Delete">
                    <span className="material-icons">delete</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        {/* Pagination */}
        <div className="pagination">
          <button className="btn-secondary">Previous</button>
          <span>Page 1 of 50</span>
          <button className="btn-secondary">Next</button>
        </div>
      </div>
    );
  };

  const renderOrders = () => (
    <div className="shop-orders">
      <div className="section-header">
        <h1>Order Management</h1>
        <div className="header-actions">
          <button className="btn-secondary">Export CSV</button>
          <button className="btn-primary">Create Order</button>
        </div>
      </div>
      
      {/* Advanced Order Filters */}
      <div className="filters-bar">
        <input type="text" placeholder="Search orders..." className="search-input" />
        <select className="filter-select">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select className="filter-select">
          <option value="">Payment Status</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
          <option value="refunded">Refunded</option>
        </select>
        <input type="date" className="filter-input" placeholder="From Date" />
        <input type="date" className="filter-input" placeholder="To Date" />
        <button className="btn-secondary">Clear Filters</button>
      </div>
      
      {/* Orders Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Products</th>
              <th>Total</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>#1234</td>
              <td>John Doe</td>
              <td>Mathematics Textbook</td>
              <td>$25.99</td>
              <td><span className="status-badge pending">Pending</span></td>
              <td><span className="status-badge delivered">Paid</span></td>
              <td>2024-01-15</td>
              <td>
                <button className="action-btn edit">View</button>
                <button className="action-btn delete">Edit</button>
              </td>
            </tr>
            <tr>
              <td>#1235</td>
              <td>Jane Smith</td>
              <td>Science Kit</td>
              <td>$89.99</td>
              <td><span className="status-badge shipped">Shipped</span></td>
              <td><span className="status-badge delivered">Paid</span></td>
              <td>2024-01-14</td>
              <td>
                <button className="action-btn edit">View</button>
                <button className="action-btn delete">Edit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  // Analytics Tab Content
  const renderAnalytics = () => (
      <div className="analytics-content">
          <div className="analytics-header">
              <h2>Analytics Dashboard</h2>
              <div className="date-filter">
                  <select value={analyticsPeriod} onChange={(e) => setAnalyticsPeriod(e.target.value)}>
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                      <option value="90d">Last 90 Days</option>
                      <option value="1y">Last Year</option>
                  </select>
              </div>
          </div>

          <div className="analytics-grid">
              <div className="analytics-card">
                  <h3>Revenue Overview</h3>
                  <div className="chart-placeholder">
                      <div className="chart-bar" style={{height: '60%'}}></div>
                      <div className="chart-bar" style={{height: '80%'}}></div>
                      <div className="chart-bar" style={{height: '45%'}}></div>
                      <div className="chart-bar" style={{height: '90%'}}></div>
                      <div className="chart-bar" style={{height: '70%'}}></div>
                      <div className="chart-bar" style={{height: '85%'}}></div>
                      <div className="chart-bar" style={{height: '75%'}}></div>
                  </div>
                  <div className="chart-labels">
                      <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                  </div>
              </div>

              <div className="analytics-card">
                  <h3>Top Products</h3>
                  <div className="top-products">
                      <div className="product-item">
                          <span>IELTS Practice Book</span>
                          <span className="sales-count">1,234 sales</span>
                      </div>
                      <div className="product-item">
                          <span>TOEFL Guide</span>
                          <span className="sales-count">987 sales</span>
                      </div>
                      <div className="product-item">
                          <span>Vocabulary Cards</span>
                          <span className="sales-count">756 sales</span>
                      </div>
                      <div className="product-item">
                          <span>Grammar Workbook</span>
                          <span className="sales-count">543 sales</span>
                      </div>
                  </div>
              </div>

              <div className="analytics-card">
                  <h3>Customer Demographics</h3>
                  <div className="demographics">
                      <div className="demo-item">
                          <span>Students (18-25)</span>
                          <div className="progress-bar">
                              <div className="progress" style={{width: '65%'}}></div>
                          </div>
                          <span>65%</span>
                      </div>
                      <div className="demo-item">
                          <span>Professionals (26-35)</span>
                          <div className="progress-bar">
                              <div className="progress" style={{width: '25%'}}></div>
                          </div>
                          <span>25%</span>
                      </div>
                      <div className="demo-item">
                          <span>Others (36+)</span>
                          <div className="progress-bar">
                              <div className="progress" style={{width: '10%'}}></div>
                          </div>
                          <span>10%</span>
                      </div>
                  </div>
              </div>

              <div className="analytics-card">
                  <h3>Conversion Rates</h3>
                  <div className="conversion-stats">
                      <div className="stat-item">
                          <span className="stat-label">Website Visitors</span>
                          <span className="stat-value">45,678</span>
                      </div>
                      <div className="stat-item">
                          <span className="stat-label">Add to Cart</span>
                          <span className="stat-value">3,456 (7.6%)</span>
                      </div>
                      <div className="stat-item">
                          <span className="stat-label">Checkout Started</span>
                          <span className="stat-value">2,789 (6.1%)</span>
                      </div>
                      <div className="stat-item">
                          <span className="stat-label">Completed Orders</span>
                          <span className="stat-value">2,345 (5.1%)</span>
                      </div>
                  </div>
              </div>
          </div>
      </div>
  );

  // Marketing Tab Content
  const renderMarketing = () => (
      <div className="marketing-content">
          <div className="marketing-header">
              <h2>Marketing Campaigns</h2>
              <button className="btn-primary" onClick={() => setShowNewCampaign(true)}>
                  <i className="fas fa-plus"></i> New Campaign
              </button>
          </div>

          <div className="campaigns-grid">
              <div className="campaign-card active">
                  <div className="campaign-header">
                      <h3>Summer Sale 2024</h3>
                      <span className="status active">Active</span>
                  </div>
                  <div className="campaign-stats">
                      <div className="stat">
                          <span className="label">Reach</span>
                          <span className="value">12,456</span>
                      </div>
                      <div className="stat">
                          <span className="label">Clicks</span>
                          <span className="value">1,234</span>
                      </div>
                      <div className="stat">
                          <span className="label">Conversions</span>
                          <span className="value">89</span>
                      </div>
                  </div>
                  <div className="campaign-actions">
                      <button className="btn-secondary">Edit</button>
                      <button className="btn-secondary">Pause</button>
                      <button className="btn-secondary">View Report</button>
                  </div>
              </div>

              <div className="campaign-card">
                  <div className="campaign-header">
                      <h3>Back to School</h3>
                      <span className="status scheduled">Scheduled</span>
                  </div>
                  <div className="campaign-stats">
                      <div className="stat">
                          <span className="label">Reach</span>
                          <span className="value">8,234</span>
                      </div>
                      <div className="stat">
                          <span className="label">Clicks</span>
                          <span className="value">567</span>
                      </div>
                      <div className="stat">
                          <span className="label">Conversions</span>
                          <span className="value">34</span>
                      </div>
                  </div>
                  <div className="campaign-actions">
                      <button className="btn-secondary">Edit</button>
                      <button className="btn-secondary">Activate</button>
                      <button className="btn-secondary">View Report</button>
                  </div>
              </div>

              <div className="campaign-card">
                  <div className="campaign-header">
                      <h3>Holiday Special</h3>
                      <span className="status paused">Paused</span>
                  </div>
                  <div className="campaign-stats">
                      <div className="stat">
                          <span className="label">Reach</span>
                          <span className="value">15,678</span>
                      </div>
                      <div className="stat">
                          <span className="label">Clicks</span>
                          <span className="value">2,345</span>
                      </div>
                      <div className="stat">
                          <span className="label">Conversions</span>
                          <span className="value">156</span>
                      </div>
                  </div>
                  <div className="campaign-actions">
                      <button className="btn-secondary">Edit</button>
                      <button className="btn-secondary">Resume</button>
                      <button className="btn-secondary">View Report</button>
                  </div>
              </div>
          </div>

          <div className="marketing-tools">
              <h3>Marketing Tools</h3>
              <div className="tools-grid">
                  <div className="tool-card">
                      <i className="fas fa-envelope"></i>
                      <h4>Email Marketing</h4>
                      <p>Create and send email campaigns to your customer base</p>
                      <button className="btn-secondary">Launch</button>
                  </div>
                  <div className="tool-card">
                      <i className="fas fa-share-alt"></i>
                      <h4>Social Media</h4>
                      <p>Schedule and manage social media posts</p>
                      <button className="btn-secondary">Launch</button>
                  </div>
                  <div className="tool-card">
                      <i className="fas fa-chart-line"></i>
                      <h4>SEO Tools</h4>
                      <p>Optimize your content for search engines</p>
                      <button className="btn-secondary">Launch</button>
                  </div>
                  <div className="tool-card">
                      <i className="fas fa-bullhorn"></i>
                      <h4>Promotions</h4>
                      <p>Create discount codes and special offers</p>
                      <button className="btn-secondary">Launch</button>
                  </div>
              </div>
          </div>
      </div>
  );

  // Settings Tab Content
  const renderSettings = () => (
      <div className="settings-content">
          <div className="settings-header">
              <h2>Shop Settings</h2>
          </div>

          <div className="settings-sections">
              <div className="settings-section">
                  <h3>General Settings</h3>
                  <div className="setting-item">
                      <label>Shop Name</label>
                      <input type="text" defaultValue="Nativelike Learning Store" />
                  </div>
                  <div className="setting-item">
                      <label>Currency</label>
                      <select defaultValue="USD">
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                      </select>
                  </div>
                  <div className="setting-item">
                      <label>Timezone</label>
                      <select defaultValue="UTC">
                          <option value="UTC">UTC</option>
                          <option value="EST">Eastern Time</option>
                          <option value="PST">Pacific Time</option>
                      </select>
                  </div>
              </div>

              <div className="settings-section">
                  <h3>Payment Settings</h3>
                  <div className="setting-item">
                      <label>Payment Gateway</label>
                      <select defaultValue="stripe">
                          <option value="stripe">Stripe</option>
                          <option value="paypal">PayPal</option>
                          <option value="square">Square</option>
                      </select>
                  </div>
                  <div className="setting-item">
                      <label>Tax Rate (%)</label>
                      <input type="number" defaultValue="8.5" min="0" max="100" step="0.1" />
                  </div>
                  <div className="setting-item">
                      <label>Shipping Cost</label>
                      <input type="number" defaultValue="5.99" min="0" step="0.01" />
                  </div>
              </div>

              <div className="settings-section">
                  <h3>Notification Settings</h3>
                  <div className="setting-item">
                      <label>
                          <input type="checkbox" defaultChecked /> Email notifications for new orders
                      </label>
                  </div>
                  <div className="setting-item">
                      <label>
                          <input type="checkbox" defaultChecked /> SMS notifications for order updates
                      </label>
                  </div>
                  <div className="setting-item">
                      <label>
                          <input type="checkbox" /> Low stock alerts
                      </label>
                  </div>
                  <div className="setting-item">
                      <label>
                          <input type="checkbox" defaultChecked /> Marketing email opt-in
                      </label>
                  </div>
              </div>

              <div className="settings-section">
                  <h3>Security Settings</h3>
                  <div className="setting-item">
                      <label>Two-Factor Authentication</label>
                      <button className="btn-secondary">Enable</button>
                  </div>
                  <div className="setting-item">
                      <label>Session Timeout (minutes)</label>
                      <input type="number" defaultValue="30" min="5" max="480" />
                  </div>
                  <div className="setting-item">
                      <label>Password Policy</label>
                      <select defaultValue="strong">
                          <option value="basic">Basic</option>
                          <option value="strong">Strong</option>
                          <option value="very-strong">Very Strong</option>
                      </select>
                  </div>
              </div>
          </div>

          <div className="settings-actions">
              <button className="btn-primary">Save Changes</button>
              <button className="btn-secondary">Reset to Defaults</button>
          </div>
      </div>
  );

  const renderTabContent = () => {
    switch(activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'products':
        return renderProducts();
      case 'orders':
        return renderOrders();
      case 'customers':
        return renderCustomers();
      case 'analytics':
        return renderAnalytics();
      case 'marketing':
        return renderMarketing();
      case 'settings':
        return renderSettings();
      default:
        return (
          <div className="tab-content-placeholder">
            <h2>{tabs.find(tab => tab.id === activeTab)?.name}</h2>
            <p>This section is under development.</p>
          </div>
        );
    }
  };

  return (
    <div className="shop-management-wrapper">
      <div className="shop-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`shop-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-icons">{tab.icon}</span>
            <span>{tab.name}</span>
          </button>
        ))}
      </div>
      
      <div className="shop-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          renderTabContent()
        )}
      </div>
          </div>
    );
  };

  const renderCustomers = () => (
    <div className="customers-content">
      <div className="customers-header">
        <h2>Customer Management</h2>
        <div className="header-actions">
          <button className="btn-primary">
            <i className="fas fa-plus"></i> Add Customer
          </button>
          <button className="btn-secondary">
            <i className="fas fa-download"></i> Export
          </button>
        </div>
      </div>

      <div className="customers-filters">
        <div className="filter-group" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search customers..." className="search-input" />
          <select className="filter-select">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="vip">VIP</option>
          </select>
          <select className="filter-select">
            <option value="">All Segments</option>
            <option value="new">New Customers</option>
            <option value="returning">Returning</option>
            <option value="loyal">Loyal</option>
          </select>
        </div>
      </div>

      <div className="customers-grid">
        <div className="customer-card">
          <div className="customer-avatar">
            <i className="fas fa-user"></i>
          </div>
          <div className="customer-info">
            <h3>John Doe</h3>
            <p>john.doe@email.com</p>
            <p>+1 (555) 123-4567</p>
            <span className="customer-segment vip">VIP Customer</span>
          </div>
          <div className="customer-stats">
            <div className="stat">
              <span className="label">Orders</span>
              <span className="value">15</span>
            </div>
            <div className="stat">
              <span className="label">Total Spent</span>
              <span className="value">$2,450</span>
            </div>
            <div className="stat">
              <span className="label">Last Order</span>
              <span className="value">2 days ago</span>
            </div>
          </div>
          <div className="customer-actions">
            <button className="btn-secondary">View Details</button>
            <button className="btn-secondary">Edit</button>
            <button className="btn-secondary">Send Email</button>
          </div>
        </div>

        <div className="customer-card">
          <div className="customer-avatar">
            <i className="fas fa-user"></i>
          </div>
          <div className="customer-info">
            <h3>Jane Smith</h3>
            <p>jane.smith@email.com</p>
            <p>+1 (555) 987-6543</p>
            <span className="customer-segment returning">Returning Customer</span>
          </div>
          <div className="customer-stats">
            <div className="stat">
              <span className="label">Orders</span>
              <span className="value">8</span>
            </div>
            <div className="stat">
              <span className="label">Total Spent</span>
              <span className="value">$1,230</span>
            </div>
            <div className="stat">
              <span className="label">Last Order</span>
              <span className="value">1 week ago</span>
            </div>
          </div>
          <div className="customer-actions">
            <button className="btn-secondary">View Details</button>
            <button className="btn-secondary">Edit</button>
            <button className="btn-secondary">Send Email</button>
          </div>
        </div>

        <div className="customer-card">
          <div className="customer-avatar">
            <i className="fas fa-user"></i>
          </div>
          <div className="customer-info">
            <h3>Mike Johnson</h3>
            <p>mike.johnson@email.com</p>
            <p>+1 (555) 456-7890</p>
            <span className="customer-segment new">New Customer</span>
          </div>
          <div className="customer-stats">
            <div className="stat">
              <span className="label">Orders</span>
              <span className="value">3</span>
            </div>
            <div className="stat">
              <span className="label">Total Spent</span>
              <span className="value">$450</span>
            </div>
            <div className="stat">
              <span className="label">Last Order</span>
              <span className="value">3 days ago</span>
            </div>
          </div>
          <div className="customer-actions">
            <button className="btn-secondary">View Details</button>
            <button className="btn-secondary">Edit</button>
            <button className="btn-secondary">Send Email</button>
          </div>
        </div>
      </div>

      <div className="pagination">
        <button className="btn-secondary">Previous</button>
        <span className="page-info">Page 1 of 5</span>
        <button className="btn-secondary">Next</button>
      </div>
    </div>
  );

export default ShopManagement; 